import React, { useEffect, useState } from 'react';
import {
  RefreshControl, ScrollView, StyleSheet, Text, View,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Application from 'expo-application';
import { useSelector } from 'react-redux';
import * as Animatable from 'react-native-animatable';
import * as Updates from 'expo-updates';
import { FontAwesome } from '@expo/vector-icons';
import Colors from '../constants/Colors';
import { RootTabScreenProps } from '../types';
import BoltIconDesktop from '../components/24pxIcons/BoltIconDesktop';
import MapIcon from '../components/MapIcon';
import GridIcon from '../components/24pxIcons/GridIcon';
import MonitorIcon from '../components/MonitorIcon';
import SunIcon from '../components/SunIcon';
import ChairIcon from '../components/ChairIcon';
import SmileIcon from '../components/SmileIcon';
import AvatarIcon from '../components/AvatarIcon';
import PeopleIcon24 from '../components/24pxIcons/PeopleIcon24';
import BuildingSelector from '../components/BuildingSelector';
import QuestionIcon from '../components/24pxIcons/QuestionIcon';
import SupportIcon from '../components/24pxIcons/SupportIcon';
import { HappyScoreCategory, HappyScoreCategoryTitles, UserPermission } from '../constants/Types';
import { getCategoryScreen } from '../components/NotificationsLog/data';
import PressableStyled from '../components/PressableStyled';
import MenuRightIcon from '../components/24pxIcons/MenuRightIcon';
import ThermometerIcon from '../components/24pxIcons/ThermometerIcon';
import { AppScreen } from '../navigation/LinkingConfiguration';
import SolveIcon24 from '../components/24pxIcons/SolveIcon24';
import OccupancyIcon from '../components/24pxIcons/OccupancyIcon';
import CoffeeIcon from '../components/CoffeeIcon';
import BotIcon from '../components/24pxIcons/BotIcon';

export default function MenuScreen({ navigation }: RootTabScreenProps<'MenuStack'>) {
  const {
    firstName, lastName, isAdmin, permissions, enabledFeatures,
  } = useSelector((state:
    {
      user: {
        firstName, lastName, isAdmin: boolean, permissions: string[],
        enabledFeatures: { chatbot: boolean },
      }
    }) => state.user);
  const isDev: boolean = useSelector((state:
    { devState: { isDev: boolean } }) => state.devState.isDev);
  const [happyEnabled, setHappyEnabled] = useState<boolean>(false);
  const [client, setClient] = useState<string>('');
  const [apiDomain, setApiDomain] = useState<string>('api.hereworks.tech');
  const [loading, setLoading] = useState<boolean>(false);
  const appUpdateTime = Updates.createdAt;

  const goToProfile: () => void = () => navigation.navigate(AppScreen.PROFILE);

  useEffect(() => {
    // else AsyncStorage.getItem('email').then((e) => setName(e));

    if (!firstName || !lastName) goToProfile();

    navigation.addListener('focus', () => {
      // console.log('focus: + ' + new Date());
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    AsyncStorage.getItem('happyEnabled').then((value) => {
      setHappyEnabled(value === 'enabled');
    });
    AsyncStorage.getItem('clientName').then(setClient);
    AsyncStorage.getItem('apiDomain').then((currentApi) => {
      setApiDomain(currentApi ?? 'api.hereworks.tech');
    });
  }, []);

  const iconColor: string = Colors.light.hereworksBlue400;

  return <View style={styles.container}>
    <>
      {!loading && <Animatable.View animation={'fadeIn'}>
        <BuildingSelector testID={'menu-screen-building-selector'} />
      </Animatable.View>}
      <ScrollView
        contentContainerStyle={{}}
        style={{ paddingHorizontal: 24, alignSelf: 'stretch' }}
        refreshControl={<RefreshControl
          refreshing={loading}
        />}
      >
        {isAdmin || permissions.includes(UserPermission.TICKETS_READ) ? (
          <>
            <View style={styles.headerContainer}>
              <Text style={styles.sectionHeader}>{'ANALYTICS'}</Text>
            </View>
            <PressableStyled
              style={styles.buttonStyleV2}
              onPress={() => {
                navigation.navigate(AppScreen.ANALYTICS);
              }}
            >
              <SolveIcon24 color={iconColor} />
              <Text style={styles.buttonTitle}>{'Solve Analytics'}</Text>
              <MenuRightIcon />
            </PressableStyled>
          </>) : null}
        {happyEnabled && <>
          <PressableStyled
            style={styles.buttonStyleV2}
            onPress={() => {
              navigation.navigate(getCategoryScreen(HappyScoreCategory.ENERGY));
            }}
          >
            <BoltIconDesktop color={iconColor} />
            <Text style={styles.buttonTitle}>
              {HappyScoreCategoryTitles[HappyScoreCategory.ENERGY]}
            </Text>
            <MenuRightIcon />
          </PressableStyled>
          <PressableStyled
            style={styles.buttonStyleV2}
            onPress={() => {
              navigation.navigate(getCategoryScreen(HappyScoreCategory.OCCUPANCY));
            }}
          >
            <OccupancyIcon color={iconColor} />
            <Text style={styles.buttonTitle}>
              {HappyScoreCategoryTitles[HappyScoreCategory.OCCUPANCY]}
            </Text>
            <MenuRightIcon />
          </PressableStyled>
        </>}
        {happyEnabled && <>
          <PressableStyled
            style={styles.buttonStyleV2}
            onPress={() => {
              navigation.navigate(getCategoryScreen(HappyScoreCategory.IT));
            }}
          >
            <MonitorIcon color={iconColor} />
            <Text style={styles.buttonTitle}>
              {HappyScoreCategoryTitles[HappyScoreCategory.IT]}
            </Text>
            <MenuRightIcon />
          </PressableStyled>
          <PressableStyled
            style={styles.buttonStyleV2}
            onPress={() => {
              navigation.navigate(getCategoryScreen(HappyScoreCategory.AMENITIES));
            }}
          >
            <CoffeeIcon color={iconColor} />
            <Text style={styles.buttonTitle}>
              {HappyScoreCategoryTitles[HappyScoreCategory.AMENITIES]}
            </Text>
            <MenuRightIcon />
          </PressableStyled>
        </>}
        {happyEnabled && <>
          <PressableStyled
            style={styles.buttonStyleV2}
            onPress={() => {
              navigation.navigate(getCategoryScreen(HappyScoreCategory.EXTERNAL_ENVIRONMENT));
            }}
          >
            <SunIcon color={iconColor} />
            <Text style={styles.buttonTitle}>
              {HappyScoreCategoryTitles[HappyScoreCategory.EXTERNAL_ENVIRONMENT]}
            </Text>
            <MenuRightIcon />
          </PressableStyled>
          <PressableStyled
            style={styles.buttonStyleV2}
            onPress={() => {
              navigation.navigate(getCategoryScreen(HappyScoreCategory.ERGONOMICS));
            }}
          >
            <View style={{
              width: 24, height: 24, justifyContent: 'center', alignItems: 'center',
            }}>
              <ChairIcon color={iconColor} />
            </View>
            <Text style={styles.buttonTitle}>
              {HappyScoreCategoryTitles[HappyScoreCategory.ERGONOMICS]}
            </Text>
            <MenuRightIcon />
          </PressableStyled>
        </>}
        {happyEnabled && <>
          <PressableStyled
            style={styles.buttonStyleV2}
            onPress={() => {
              navigation.navigate(getCategoryScreen(HappyScoreCategory.INTERNAL_ENVIRONMENT));
            }}
          >
            <ThermometerIcon color={iconColor} />
            <Text style={styles.buttonTitle}>
              {HappyScoreCategoryTitles[HappyScoreCategory.INTERNAL_ENVIRONMENT]}
            </Text>
            <MenuRightIcon />
          </PressableStyled>
          <PressableStyled
            style={styles.buttonStyleV2}
            onPress={() => {
              navigation.navigate(getCategoryScreen(HappyScoreCategory.SATISFACTION));
            }}
          >
            <SmileIcon color={iconColor} />
            <Text style={styles.buttonTitle}>
              {HappyScoreCategoryTitles[HappyScoreCategory.SATISFACTION]}
            </Text>
            <MenuRightIcon />
          </PressableStyled>
        </>}
        <View style={{ marginBottom: 9 }}>
          <View style={styles.headerContainer}>
            <Text style={styles.sectionHeader}>{'MAP'}</Text>
          </View>
          <PressableStyled
            style={styles.buttonStyleV2}
            onPress={() => {
              navigation.navigate(AppScreen.MAP);
            }}
          >
            <MapIcon color={iconColor} />
            <Text style={styles.buttonTitle}>{'Map'}</Text>
            <MenuRightIcon />
          </PressableStyled>
        </View>
        <View style={{ marginBottom: 9 }}>
          <View style={styles.headerContainer}>
            <Text style={styles.sectionHeader}>{'MANAGEMENT'}</Text>
          </View>
          {isAdmin || permissions.includes(UserPermission.ASSETS_READ) ? (
            <PressableStyled
              style={styles.buttonStyleV2}
              onPress={() => {
                const screen = AppScreen.ASSET_MANAGEMENT;
                navigation.navigate(screen as any, { assetsView: true });
              }}
            >
              <GridIcon color={iconColor} />
              <Text style={styles.buttonTitle}>{'Assets'}</Text>
              <MenuRightIcon />
            </PressableStyled>) : null}
          {isAdmin || permissions.includes(UserPermission.TEMPLATES_READ) ? <>
            <PressableStyled
              style={styles.buttonStyleV2}
              onPress={() => {
                navigation.navigate(AppScreen.TEMPLATE_MANAGEMENTV2);
              }}
            >
              <GridIcon color={iconColor} fill={iconColor} />
              <Text style={styles.buttonTitle}>{'Templates'}</Text>
              <MenuRightIcon />
            </PressableStyled>
            {isDev && <PressableStyled
              style={styles.buttonStyleV2}
              onPress={() => {
                navigation.navigate(AppScreen.TEMPLATE_MANAGEMENT);
              }}
            >
              <GridIcon color={Colors.light.hereworksYellow500} fill={iconColor} />
              <Text style={styles.buttonTitle}>{'Old Template management'}</Text>
              <MenuRightIcon />
            </PressableStyled>}
          </> : null}
          {isAdmin || permissions.includes(UserPermission.USER_GROUPS_READ)
            || permissions.includes(UserPermission.USERS_READ) ? (
            <PressableStyled
              style={styles.buttonStyleV2}
              onPress={() => {
                navigation.navigate(AppScreen.USER_MANAGEMENT);
              }}
            >
              <PeopleIcon24 />
              <Text style={styles.buttonTitle}>{'Users'}</Text>
              <MenuRightIcon />
            </PressableStyled>) : null}
        </View>
        <View style={{ marginBottom: 9 }}>
          <View style={styles.headerContainer}>
            <Text style={styles.sectionHeader}>{'SUPPORT'}</Text>
          </View>
          <PressableStyled
            style={[styles.buttonStyleV2, { flex: 1 }]}
            onPress={() => navigation.navigate(AppScreen.SUPPORT)}
          >
            <SupportIcon color={iconColor} />
            <Text style={styles.buttonTitle}>{'Request support'}</Text>
            <MenuRightIcon />
          </PressableStyled>
          <PressableStyled
            style={[styles.buttonStyleV2, { flex: 1 }]}
            onPress={() => {
              navigation.navigate(AppScreen.KNOWLEDGE_CENTER);
            }}
          >
            <QuestionIcon color={iconColor} />
            <Text style={styles.buttonTitle}>{'Knowledge center'}</Text>
            <MenuRightIcon />
          </PressableStyled>
          {enabledFeatures.chatbot ? <PressableStyled style={styles.buttonStyleV2}
            onPress={() => navigation.navigate(AppScreen.HAPPY_CHAT_V2)}>
            <BotIcon />
            <Text style={styles.buttonTitle}>Insights Agent</Text>
            <MenuRightIcon />
          </PressableStyled> : null}
          {isDev ? <PressableStyled style={styles.buttonStyleV2}
            onPress={() => navigation.navigate(AppScreen.GRAPH_PROTOTYPE)}>
            <FontAwesome name='line-chart' size={20} color={Colors.light.hereworksBlue500} />
            <Text style={styles.buttonTitle}>Graph Prototype</Text>
            <MenuRightIcon />
          </PressableStyled> : null}
          {isDev ? <PressableStyled style={styles.buttonStyleV2}
            onPress={() => navigation.navigate(AppScreen.TEST_WEB_VIEW)}>
            <FontAwesome name='globe' size={20} color={Colors.light.hereworksBlue500} />
            <Text style={styles.buttonTitle}>Test Web View</Text>
            <MenuRightIcon />
          </PressableStyled> : null}
          {isDev ? <PressableStyled style={styles.buttonStyleV2}
            onPress={() => navigation.navigate(AppScreen.PDFViewer)}>
            <FontAwesome name='file-pdf-o' size={20} color={Colors.light.hereworksBlue500} />
            <Text style={styles.buttonTitle}>Ts & Cs PDF</Text>
            <MenuRightIcon />
          </PressableStyled> : null}
        </View>
        <View style={{ alignSelf: 'center', marginBottom: 8 }}>
          <Text style={styles.versionText}>
            {'App Version: '}
            {Application.nativeApplicationVersion}
            {` (${Application.nativeBuildVersion})`}
            {appUpdateTime && `\nApp last updated at: ${appUpdateTime.toLocaleString()}`}
            {`\nClient: ${client}\nAPI: ${apiDomain}`}
            {Updates.channel && ` (${Updates.channel})`}
          </Text>
        </View>
      </ScrollView>
      <PressableStyled
        style={styles.profileScreenButton}
        onPress={goToProfile}
        testID='my-profile-section-btn'
      >
        <AvatarIcon />
        <View style={{ marginLeft: 12 }}>
          <Text style={styles.name}>
            {`${firstName ?? ''} ${lastName ?? ''}`}
          </Text>
          <Text style={styles.viewProfileText}>
            {'View profile'}
          </Text>
        </View>
      </PressableStyled>
    </>
  </View>;
}

const styles = StyleSheet.create({
  container: {
    justifyContent: 'flex-start',
    backgroundColor: '#fff',
    flex: 1,
    paddingTop: 0,
  },
  buttonStyle: {
    paddingLeft: 10,
    backgroundColor: Colors.light.hereworksWhite100,
    borderRadius: 8,
    marginBottom: 8,
    flexDirection: 'row',
    alignSelf: 'stretch',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    height: 44,
  },
  buttonStyleV2: {
    paddingLeft: 10,
    marginBottom: 8,
    flexDirection: 'row',
    alignSelf: 'stretch',
    alignItems: 'center',
    height: 44,
  },
  buttonStyleYellow: {
    padding: 10,
    backgroundColor: Colors.light.hereworksYellow50,
    borderRadius: 8,
    marginBottom: 8,
    flexDirection: 'row',
    alignSelf: 'stretch',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: Colors.light.hereworksYellow200,
  },
  sectionHeader: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 12,
    color: Colors.light.hereworksBlack300,
    lineHeight: 18,
    marginBottom: 4,
    marginTop: 12,
  },
  buttonTitle: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksBlack600,
    marginLeft: 10,
    flexWrap: 'wrap',
    flex: 1,
  },
  profileScreenButton: {
    paddingHorizontal: 24,
    paddingVertical: 16,
    flexDirection: 'row',
    borderTopWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
  },
  name: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
    color: Colors.light.hereworksBlack600,
    marginBottom: 4,
  },
  viewProfileText: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 12,
    color: Colors.light.hereworksBlue400,
  },
  versionText: {
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Roboto_400Regular',
    textAlign: 'center',
  },
  headerContainer: { borderBottomWidth: 1, borderColor: Colors.light.hereworksBlue50 },
});
